import * as React from 'react';
import classNames from 'classnames';
import { Component } from 'react';
import propz from 'propz';
import debounce from 'debounce-promise';
import { createPortal } from 'react-dom';
import './Autocomplete.css';

interface Props {
  onSelect: (item: any) => void;
  searchFunction: (value) => any;
  getElementTitle: (item: any) => string;
  defaultItem?: any;
  customClass?: string;
  disabled?: boolean;
  placeholder?: string;
  isItemWithImage?: boolean;
  fallbackImage?: string;
  tabIndex?: number;
  showAllOnFocus?: boolean;
  selectTextOnFocus?: boolean;
  debounceMs?: number;
  renderItem?: (item: any) => React.ReactNode;
  renderItemMeta?: (item: any) => React.ReactNode;
  emptyMessage?: React.ReactNode;
  onInputChange?: (value: string) => void;
  isMenuPortal?: boolean;
  menuMaxHeight?: number;
  menuZIndex?: number;
  clearOnSelect?: boolean;
}

interface State {
  isLoading: boolean;
  itemList: any[];
  isOpen: boolean;
  currentText: string;
}

export class Autocomplete extends Component<Props, State> {
  searchFunctionDebounced: any;
  inputRef = React.createRef<HTMLInputElement>();
  searchRequestId = 0;
  isEditing = false;

  static defaultProps = {
    disabled: false,
    debounceMs: 500
  };

  constructor(props) {
    super(props);

    this.state = {
      isLoading: false,
      itemList: [],
      isOpen: false,
      currentText: ''
    };
  }

  componentDidMount() {
    const { searchFunction, defaultItem, getElementTitle, debounceMs } = this.props;
    this.searchFunctionDebounced = debounce(searchFunction, debounceMs);
    if (typeof defaultItem !== 'undefined') {
      this.setState({
        currentText: getElementTitle(defaultItem)
      });
    }
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.closeMenu);
  }

  /** Checks on new props if we need to set default item */
  UNSAFE_componentWillReceiveProps(nextProps) {
    const { defaultItem, getElementTitle } = this.props;
    const { defaultItem: nextDefaultItem } = nextProps;
    // Eliminating infinite looping by checking if really props update take place
    if (nextDefaultItem && JSON.stringify(nextDefaultItem) !== JSON.stringify(defaultItem)) {
      // Change current text (value in input field) on props.defaultItem, if received new props.defaultItem
      this.setState({
        currentText: getElementTitle(nextDefaultItem)
      });
    }
    if (typeof defaultItem !== 'undefined' && typeof nextDefaultItem === 'undefined') {
      if (!this.isEditing) {
        this.setState({
          currentText: ''
        });
      }
      this.isEditing = false;
    }
  }

  onChange = event => {
    event.preventDefault();
    const value = event.target.value;
    this.isEditing = true;
    this.search(value);
    if (typeof this.props.onInputChange === 'function') {
      this.props.onInputChange(value);
    }
  };

  onFocus = event => {
    const { currentText } = this.state;
    const { showAllOnFocus, selectTextOnFocus } = this.props;

    if (selectTextOnFocus) {
      event.target.select();
    }

    this.search(showAllOnFocus ? '' : currentText, currentText);
  };

  search(value: string, nextText?: string) {
    const requestId = ++this.searchRequestId;
    this.setState({
      isLoading: true,
      currentText: typeof nextText !== 'undefined' ? nextText : value
    });

    this.searchFunctionDebounced(value)
      .then(data => {
        if (requestId !== this.searchRequestId) return;
        this.setState(
          {
            isLoading: false,
            itemList: Array.isArray(data) ? data : [],
            isOpen: true
          },
          () => {
            document.addEventListener('click', this.closeMenu);
          }
        );
      })
      .catch(() => {
        if (requestId !== this.searchRequestId) return;
        this.setState(
          {
            isLoading: false,
            itemList: [],
            isOpen: true
          },
          () => {
            document.addEventListener('click', this.closeMenu);
          }
        );
      });
  }

  closeMenu = () => {
    this.setState({ isOpen: false }, () => {
      document.removeEventListener('click', this.closeMenu);
    });
  };

  onItemClick = (event, item) => {
    event.preventDefault();
    this.isEditing = false;
    const currentText = this.props.clearOnSelect ? '' : this.props.getElementTitle(item);

    //don't remember remove listener
    document.removeEventListener('click', this.closeMenu);

    this.setState({
      currentText: currentText,
      isOpen: false
    });

    this.props.onSelect(item);
  };

  renderItems() {
    const { itemList } = this.state;
    const { isItemWithImage, renderItem, renderItemMeta, fallbackImage, emptyMessage } = this.props;

    if (itemList.length == 0) {
      return (
        <div className="eAutocompleteEmpty" role="status">
          {emptyMessage || 'No results'}
        </div>
      );
    }

    return itemList.map((item, index) => {
      if (isItemWithImage) {
        const postcode = propz.get(item, ['postcode', 'postcode'], '');
        const address = propz.get(item, ['address'], '');
        const pic = propz.get(item, ['pic'], '');
        const title = this.props.getElementTitle(item);
        const initials =
          title
            .split(/\s+/)
            .filter(Boolean)
            .slice(0, 2)
            .map(part => part.charAt(0).toUpperCase())
            .join('') || 'S';

        const subtitleParts = [postcode, address].filter(Boolean);
        const subtitle = subtitleParts.length > 0 ? `Address: ${subtitleParts.join(', ')}` : '';

        return (
          <a
            href=""
            className="dropdown-item eAutocompleteSchoolItem"
            key={`autocomplete_${index}`}
            onMouseDown={event => this.onItemClick(event, item)}
          >
            <div className="bSchoolListItem">
              <div className="eSchoolListItem_wrapper">
                <div className="eSchoolListItem_wrapper_pic">
                  {pic || fallbackImage ? (
                    <img className="eSchoolListItem_pic" src={pic || fallbackImage} alt={title} />
                  ) : (
                    <span className="eSchoolListItem_initials" aria-hidden="true">
                      {initials}
                    </span>
                  )}
                </div>

                <div className="eSchoolListItem_content">
                  <div className="eSchoolListItem_name">{title}</div>

                  {subtitle ? <div className="eSchoolListItem_address">{subtitle}</div> : null}
                  {renderItemMeta ? <div className="eSchoolListItem_meta">{renderItemMeta(item)}</div> : null}
                </div>
              </div>
            </div>
          </a>
        );
      } else {
        return (
          <a
            href=""
            className="dropdown-item text-wrap eAutocompleteSimpleItem"
            key={`autocomplete_${index}`}
            onMouseDown={event => this.onItemClick(event, item)}
          >
            <div className="eAutocompleteSimpleItem__text">
              {renderItem ? renderItem(item) : this.props.getElementTitle(item)}
            </div>
          </a>
        );
      }
    });
  }

  getCurrentText(): string {
    const { currentText } = this.state;
    const { defaultItem, getElementTitle } = this.props;

    switch (true) {
      case typeof currentText !== 'undefined':
        return currentText;
      case typeof defaultItem !== 'undefined':
        return getElementTitle(defaultItem);
      default:
        return '';
    }
  }

  onKeyPress = event => {
    if (event.key === 'Tab') {
      this.setState({ isOpen: false });
    }
  };

  render() {
    const { isLoading, isOpen } = this.state;

    const {
      customClass,
      disabled,
      placeholder,
      tabIndex,
      isMenuPortal,
      menuMaxHeight = 280,
      menuZIndex = 1200
    } = this.props;
    const autocompleteClass = classNames('bAutocomplete', customClass);
    const inputRect = isOpen && this.inputRef.current ? this.inputRef.current.getBoundingClientRect() : null;
    const availableBelow = inputRect ? window.innerHeight - inputRect.bottom - 12 : 0;
    const availableAbove = inputRect ? inputRect.top - 12 : 0;
    const openAbove = inputRect && availableBelow < 160 && availableAbove > availableBelow;
    const portalMenu = inputRect ? (
      <div
        className="dropdown-menu show eShowDropdownMenu eAutocompletePortalMenu"
        style={
          {
            '--autocomplete-menu-width': `${inputRect.width}px`,
            position: 'fixed',
            zIndex: menuZIndex,
            left: inputRect.left,
            top: openAbove ? 'auto' : inputRect.bottom + 6,
            bottom: openAbove ? window.innerHeight - inputRect.top + 6 : 'auto',
            width: inputRect.width,
            minWidth: inputRect.width,
            maxWidth: inputRect.width,
            maxHeight: Math.min(menuMaxHeight, Math.max(120, openAbove ? availableAbove : availableBelow)),
            overflowY: 'auto'
          } as React.CSSProperties
        }
        onMouseDown={event => event.stopPropagation()}
      >
        {this.renderItems()}
      </div>
    ) : null;

    return (
      <div className={autocompleteClass}>
        <input
          ref={this.inputRef}
          className="form-control eAutocompleteInput"
          value={this.getCurrentText()}
          onChange={this.onChange}
          onFocus={this.onFocus}
          onKeyDown={this.onKeyPress}
          disabled={disabled}
          placeholder={placeholder}
          tabIndex={tabIndex}
        />
        {isLoading && <div className={'spinner-border spinner-border-sm eAutocompleteSpinner'} />}

        {isOpen && !isMenuPortal && <div className="dropdown-menu show eShowDropdownMenu">{this.renderItems()}</div>}
        {isOpen && isMenuPortal && portalMenu && createPortal(portalMenu, document.body)}
      </div>
    );
  }
}
